/**
 * LiveModeScreen - Real-time voice conversation with Gemini Live API
 *
 * Architecture: Browser → WebSocket → orbit-gateway → Gemini Live API
 *
 * Features:
 * - Continuous PCM16 audio streaming at 16kHz
 * - Real-time Gemini audio playback at 24kHz
 * - Live transcription (user + AI)
 * - Visual audio waveform
 * - Interruption handling (barge-in)
 * - Connection lifecycle management
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { SpaceBackground, AtomIcon } from '../components/shared';
import {
  X, Mic, MicOff, Volume2, VolumeX, Phone, PhoneOff,
  Loader, Radio
} from 'lucide-react';
import { getIdToken } from '../services/firebase';

// Derive WebSocket URL from the gateway URL
const ORBIT_GATEWAY_URL =
  import.meta.env.VITE_ORBIT_GATEWAY_URL ||
  'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';

function getWsUrl(): string {
  return ORBIT_GATEWAY_URL
    .replace('https://', 'wss://')
    .replace('http://', 'ws://') + '/ws/live';
}

// Audio configuration
const SEND_SAMPLE_RATE = 16000;   // PCM16 to Gemini
const RECEIVE_SAMPLE_RATE = 24000; // PCM from Gemini
const BUFFER_SIZE = 4096;

// ─── Audio Visualizer ────────────────────────────────────────────────────────

interface AudioVisualizerProps {
  isActive: boolean;
  isSpeaking: boolean;
}

const AudioVisualizer = ({ isActive, isSpeaking }: AudioVisualizerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2;
    const bars = 40;
    const barWidth = width / bars - 2;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < bars; i++) {
        const x = i * (barWidth + 2);
        const amplitude = isActive
          ? isSpeaking
            ? 30 + Math.sin(Date.now() / 100 + i * 0.5) * 20
            : 10 + Math.sin(Date.now() / 200 + i * 0.3) * 5
          : 3;
        const barHeight = amplitude + Math.random() * (isActive ? 10 : 2);

        const gradient = ctx.createLinearGradient(
          x, centerY - barHeight, x, centerY + barHeight
        );
        gradient.addColorStop(0, isSpeaking ? 'rgba(34, 211, 238, 0.8)' : 'rgba(34, 211, 238, 0.4)');
        gradient.addColorStop(0.5, isSpeaking ? 'rgba(34, 211, 238, 1)' : 'rgba(34, 211, 238, 0.6)');
        gradient.addColorStop(1, isSpeaking ? 'rgba(34, 211, 238, 0.8)' : 'rgba(34, 211, 238, 0.4)');

        ctx.fillStyle = gradient;
        ctx.fillRect(x, centerY - barHeight, barWidth, barHeight * 2);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isActive, isSpeaking]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={100}
      className="w-full max-w-[300px] h-[100px]"
    />
  );
};

// ─── PCM Audio Helpers ───────────────────────────────────────────────────────

/** Convert Float32 audio samples to Int16 PCM and return as base64. */
function float32ToBase64Pcm16(samples: Float32Array): string {
  const pcm = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    pcm[i] = Math.max(-32768, Math.min(32767, Math.round(samples[i] * 32768)));
  }
  const bytes = new Uint8Array(pcm.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Decode base64 PCM audio and enqueue for playback. */
function decodeBase64Pcm(base64: string): Int16Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
}

/** Convert Int16 PCM samples to Float32 for AudioBuffer. */
function int16ToFloat32(pcm: Int16Array): Float32Array {
  const float = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    float[i] = pcm[i] / 32768;
  }
  return float;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function LiveModeScreen() {
  const navigate = useNavigate();
  const { } = useAuthContext();

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Audio state
  const [isMuted, setIsMuted] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);

  // Transcript state
  const [transcript, setTranscript] = useState<{ role: 'user' | 'ai'; text: string }[]>([]);
  const [currentAiText, setCurrentAiText] = useState('');

  // Refs for cleanup
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const isMutedRef = useRef(false);
  const isSpeakerMutedRef = useRef(false);
  const nextPlayTimeRef = useRef(0);

  // Keep refs in sync with state
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
  useEffect(() => { isSpeakerMutedRef.current = isSpeakerMuted; }, [isSpeakerMuted]);

  // ── Play Gemini audio response ──────────────────────────────────────────

  const playAudioChunk = useCallback((pcmBase64: string) => {
    if (isSpeakerMutedRef.current) return;

    let ctx = playbackContextRef.current;
    if (!ctx || ctx.state === 'closed') {
      ctx = new AudioContext({ sampleRate: RECEIVE_SAMPLE_RATE });
      playbackContextRef.current = ctx;
      nextPlayTimeRef.current = 0;
    }

    try {
      const pcm16 = decodeBase64Pcm(pcmBase64);
      const float32 = int16ToFloat32(pcm16);

      const buffer = ctx.createBuffer(1, float32.length, RECEIVE_SAMPLE_RATE);
      buffer.copyToChannel(new Float32Array(float32), 0);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);

      // Schedule seamless playback
      const now = ctx.currentTime;
      const startTime = Math.max(now, nextPlayTimeRef.current);
      source.start(startTime);
      nextPlayTimeRef.current = startTime + buffer.duration;
    } catch (e) {
      console.error('[Live] Error playing audio chunk:', e);
    }
  }, []);

  // ── Start live session ──────────────────────────────────────────────────

  const startSession = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    setTranscript([]);
    setCurrentAiText('');

    try {
      // 1. Get Firebase auth token
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');

      // 2. Request microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SEND_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      mediaStreamRef.current = stream;

      // 3. Set up capture AudioContext at 16kHz
      const audioCtx = new AudioContext({ sampleRate: SEND_SAMPLE_RATE });
      audioContextRef.current = audioCtx;

      // 4. Connect WebSocket
      const wsUrl = getWsUrl();
      console.log('[Live] Connecting to', wsUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[Live] WebSocket connected, sending init');

        // Send init message with token + config
        ws.send(JSON.stringify({
          type: 'init',
          token,
          config: {
            voice: 'Puck',
            systemPrompt:
              'You are SuperJarvis, an intelligent AI assistant created by Bill. ' +
              'Sophisticated personality with dry wit and quiet confidence. ' +
              'Be helpful, concise, and natural in conversation. ' +
              'No markdown, no bullet points — speak naturally.',
          },
        }));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case 'connected':
              console.log('[Live] Session established, model:', msg.model);
              setIsConnected(true);
              setIsConnecting(false);

              // Start audio capture after connection confirmed
              startAudioCapture(audioCtx, stream, ws);
              break;

            case 'session_ready':
              console.log('[Live] Gemini session ready');
              break;

            case 'audio':
              // Play Gemini's audio response
              setIsAiSpeaking(true);
              playAudioChunk(msg.data);
              break;

            case 'transcript':
              if (msg.role === 'user' && msg.text) {
                setTranscript((prev) => [...prev, { role: 'user', text: msg.text }]);
              } else if (msg.role === 'ai' && msg.text) {
                if (msg.final) {
                  setTranscript((prev) => [...prev, { role: 'ai', text: msg.text }]);
                  setCurrentAiText('');
                } else {
                  setCurrentAiText((prev) => prev + msg.text);
                }
              }
              break;

            case 'turn_complete':
              setIsAiSpeaking(false);
              if (currentAiText) {
                setTranscript((prev) => [...prev, { role: 'ai', text: currentAiText }]);
                setCurrentAiText('');
              }
              break;

            case 'interrupted':
              // User interrupted AI — stop playback
              setIsAiSpeaking(false);
              nextPlayTimeRef.current = 0;
              break;

            case 'error':
              console.error('[Live] Server error:', msg.message);
              setError(msg.message);
              break;
          }
        } catch (e) {
          console.error('[Live] Error parsing message:', e);
        }
      };

      ws.onerror = () => {
        console.error('[Live] WebSocket error');
        setError('Connection error. Please try again.');
        setIsConnecting(false);
      };

      ws.onclose = (event) => {
        console.log('[Live] WebSocket closed', event.code, event.reason);
        setIsConnected(false);
        setIsConnecting(false);
        cleanupAudio();
      };
    } catch (err) {
      console.error('[Live] Error starting session:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to start live session'
      );
      setIsConnecting(false);
      cleanupAudio();
    }
  }, [playAudioChunk]);

  // ── Start capturing and streaming mic audio ─────────────────────────────

  const startAudioCapture = useCallback(
    (audioCtx: AudioContext, stream: MediaStream, ws: WebSocket) => {
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(BUFFER_SIZE, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (isMutedRef.current || ws.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);
        const base64 = float32ToBase64Pcm16(inputData);

        ws.send(JSON.stringify({ type: 'audio', data: base64 }));
      };

      source.connect(processor);
      // Connect to destination to keep processing alive
      // (ScriptProcessorNode requires connection to destination)
      processor.connect(audioCtx.destination);
    },
    []
  );

  // ── Stop live session ───────────────────────────────────────────────────

  const stopSession = useCallback(() => {
    // Send end message
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'end' }));
      } catch { /* ignore */ }
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    cleanupAudio();
    setIsConnected(false);
    setIsAiSpeaking(false);
    setCurrentAiText('');
  }, []);

  // ── Audio cleanup ───────────────────────────────────────────────────────

  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (playbackContextRef.current && playbackContextRef.current.state !== 'closed') {
      playbackContextRef.current.close().catch(() => {});
      playbackContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    nextPlayTimeRef.current = 0;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSession();
    };
  }, [stopSession]);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <header className="p-6 flex items-center justify-between">
          <button
            onClick={() => {
              stopSession();
              navigate(-1);
            }}
            className="p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] border border-cyan-400/30 transition-all"
          >
            <X className="w-6 h-6 text-cyan-400" />
          </button>
          <div className="flex items-center space-x-3">
            <Radio className="w-5 h-5 text-cyan-400" />
            <span className="text-cyan-300 font-semibold">Live Mode</span>
            {isConnected && (
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            )}
          </div>
          <div className="w-12" />
        </header>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {/* Avatar / Visualizer */}
          <div className="relative mb-8">
            <div
              className={`w-40 h-40 rounded-full flex items-center justify-center transition-all duration-500 ${
                isConnected
                  ? isAiSpeaking
                    ? 'bg-cyan-400/30 shadow-[0_0_60px_rgba(34,211,238,0.6)]'
                    : 'bg-cyan-400/20 shadow-[0_0_40px_rgba(34,211,238,0.4)]'
                  : 'bg-cyan-400/10 shadow-[0_0_20px_rgba(34,211,238,0.2)]'
              }`}
            >
              <AtomIcon
                className={`w-20 h-20 text-cyan-400 ${
                  isConnected ? 'animate-spin-slow' : ''
                }`}
              />
            </div>
            {isAiSpeaking && (
              <div className="absolute -inset-4 rounded-full border-2 border-cyan-400/50 animate-ping" />
            )}
          </div>

          {/* Audio Visualizer */}
          <div className="mb-8">
            <AudioVisualizer
              isActive={isConnected}
              isSpeaking={isAiSpeaking}
            />
          </div>

          {/* Status Text */}
          <div className="text-center mb-8">
            {isConnecting && (
              <div className="flex items-center space-x-2 text-cyan-400">
                <Loader className="w-5 h-5 animate-spin" />
                <span>Connecting to Gemini Live...</span>
              </div>
            )}
            {isConnected && !isAiSpeaking && (
              <p className="text-cyan-400/70">Listening... Start speaking</p>
            )}
            {isAiSpeaking && (
              <p className="text-cyan-300 animate-pulse">
                SuperJarvis is speaking...
              </p>
            )}
            {error && <p className="text-red-400">{error}</p>}
          </div>

          {/* Current AI Response (streaming) */}
          {currentAiText && (
            <div className="max-w-md p-4 bg-cyan-400/10 border border-cyan-400/30 rounded-2xl mb-8">
              <p className="text-cyan-300">{currentAiText}</p>
            </div>
          )}

          {/* Transcript (last 5 messages) */}
          {transcript.length > 0 && (
            <div className="w-full max-w-md max-h-40 overflow-y-auto mb-8 space-y-2">
              {transcript.slice(-5).map((msg, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-cyan-400/10 text-cyan-300 ml-8'
                      : 'bg-cyan-400/20 text-cyan-200 mr-8'
                  }`}
                >
                  {msg.text}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Control Buttons */}
        <div className="p-6 flex items-center justify-center space-x-6 pb-12">
          {/* Mute Mic */}
          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`p-4 rounded-full transition-all ${
              isMuted
                ? 'bg-red-400/20 border border-red-400/50'
                : 'bg-cyan-400/10 border border-cyan-400/30 hover:bg-cyan-400/20'
            }`}
          >
            {isMuted ? (
              <MicOff className="w-6 h-6 text-red-400" />
            ) : (
              <Mic className="w-6 h-6 text-cyan-400" />
            )}
          </button>

          {/* Call Button */}
          {!isConnected ? (
            <button
              onClick={startSession}
              disabled={isConnecting}
              className="p-6 rounded-full bg-green-400 text-black hover:bg-green-300 transition-all shadow-[0_0_30px_rgba(74,222,128,0.5)] disabled:opacity-50"
            >
              {isConnecting ? (
                <Loader className="w-8 h-8 animate-spin" />
              ) : (
                <Phone className="w-8 h-8" />
              )}
            </button>
          ) : (
            <button
              onClick={stopSession}
              className="p-6 rounded-full bg-red-500 text-white hover:bg-red-400 transition-all shadow-[0_0_30px_rgba(239,68,68,0.5)]"
            >
              <PhoneOff className="w-8 h-8" />
            </button>
          )}

          {/* Mute Speaker */}
          <button
            onClick={() => setIsSpeakerMuted(!isSpeakerMuted)}
            className={`p-4 rounded-full transition-all ${
              isSpeakerMuted
                ? 'bg-red-400/20 border border-red-400/50'
                : 'bg-cyan-400/10 border border-cyan-400/30 hover:bg-cyan-400/20'
            }`}
          >
            {isSpeakerMuted ? (
              <VolumeX className="w-6 h-6 text-red-400" />
            ) : (
              <Volume2 className="w-6 h-6 text-cyan-400" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
